import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

// CONSTANTS
import { ROUTE_SHOWS_AND_MOVIES } from '@/constants/routes';

// UTILITY
import { externalLink, getExternalUrlParams } from '@/utility/analytics';
import { updateUrl } from '@/utility/common';
import { routeEnhance } from '@/utility/routes';

// COMPONENTS
import ClickToCopy from '@/components/ClickToCopy';
import AlertWarning from '@/components/Alerts/AlertWarning';

// HELP
import SubscriptionStepTemplate from '../SubscriptionStepTemplate';
import DialogSuccess from './help/DialogSuccess';

const RegistrationSuccessPromocode = ({ breadcrumbs, partner, dictionary, analyticsLocation }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const { unique_id } = useSelector(({ auth }) => ({
    unique_id: auth.unique_id || '',
  }));

  const { app, paymentInfo } = dictionary;
  const promoCode = paymentInfo.promo_code;
  const serviceLink = paymentInfo.service_link;
  const externalUrlParams = getExternalUrlParams({ unique_id });
  const href = serviceLink ? updateUrl(serviceLink, externalUrlParams) : '';

  function handleClaimPromoCode() {
    const properties = {
      location: analyticsLocation,
      href,
      app_id: app.id,
      type: 'app',
      service_name: app.name,
      is_affiliate: !!app.is_affiliate,
      partner: partner?.slug,
      partner_id: partner?.id,
    };

    if (href) externalLink(properties);

    setShowSuccessDialog(true);
  }

  function handleCloseSuccessDialog() {
    setShowSuccessDialog(false);
  }

  function handleGotItClick() {
    setTimeout(() => {
      const routeEnhanced = routeEnhance(ROUTE_SHOWS_AND_MOVIES, {
        state: {
          location: analyticsLocation,
        },
      });

      navigate(routeEnhanced, { replace: true });
    }, 200);

    handleCloseSuccessDialog();
  }

  return (
    <>
      <SubscriptionStepTemplate
        breadcrumbs={breadcrumbs}
        title={t('subscription.promocode.title')}
        description={t('subscription.promocode.subtitle')}
        form={
          <div>
            {!promoCode && (
              <AlertWarning
                message={t('subscription.promocode.transactionPending')}
                hasTitle={false}
                hasCloseAction={false}
              />
            )}
            {!!promoCode && (
              <div className="promocode">
                <ClickToCopy
                  copyText={promoCode}
                  message={t('notification.successfullyCopiedPromoCode')}
                >
                  <Typography component="p" variant="h4">
                    {promoCode}
                  </Typography>
                </ClickToCopy>
              </div>
            )}
            {!!serviceLink && (
              <Button
                className="step-btn"
                color="primary"
                component="a"
                href={serviceLink}
                target="_blank"
                onClick={handleClaimPromoCode}
              >
                {t('buttons.continue')}
              </Button>
            )}
          </div>
        }
      />
      {!!app && (
        <DialogSuccess
          open={!!showSuccessDialog}
          onGotItClick={handleGotItClick}
          onClose={handleCloseSuccessDialog}
          data={{ app } || {}}
          promoCode={promoCode}
        />
      )}
    </>
  );
};

RegistrationSuccessPromocode.propTypes = {
  breadcrumbs: PropTypes.node.isRequired,
  partner: PropTypes.object.isRequired,
  dictionary: PropTypes.object.isRequired,
  analyticsLocation: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default React.memo(RegistrationSuccessPromocode);
